import React, { useEffect, useState, useMemo } from 'react';
import { Eye, EyeOff, Menu, ChevronRight, Shield, Check, X as XIcon } from 'lucide-react';
import { NEU } from '../neuStyles';
import PrintButton from '../components/PrintButton';
import { previewableRoleKeys, previewableRoleLabels } from '../lib/roleCatalog';

// Sidebar sections and their items — mirrors Sidebar.js DEFAULT_SECTIONS
const SIDEBAR_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', permission: 'dashboard:read' },
    ],
  },
  {
    label: 'Clinical',
    items: [
      { id: 'patients', label: 'Patients', permission: 'patients:read' },
      { id: 'encounters', label: 'Encounters', permission: 'encounters:read' },
      { id: 'labs', label: 'Lab Results', permission: 'labs:read' },
      { id: 'medications', label: 'Medications', permission: 'medications:read' },
      { id: 'vitals', label: 'Vitals', permission: 'vitals:read' },
      { id: 'allergies', label: 'Allergies', permission: 'allergies:read' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { id: 'chat', label: 'Secure Chat', permission: 'chat:read' },
      { id: 'email', label: 'Secure Email', permission: 'email:read' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'airesearch', label: 'AI Research', permission: 'ai_research:read' },
      { id: 'bi', label: 'BI Dashboard', permission: 'bi:read' },
      { id: 'adminai', label: 'Admin AI', permission: 'admin_ai:read' },
      { id: 'research', label: 'Research Studies', permission: 'bi:read' },
      { id: 'biolab', label: 'Biolab AI', permission: 'bi:read' },
      { id: 'reports', label: 'Report Builder', permission: 'reports:read' },
      { id: 'aialerts', label: 'AI Alerts', permission: 'alerts:read' },
      { id: 'competitiveintel', label: 'Competitive Intel', permission: 'bi:read' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'billing', label: 'Billing', permission: 'billing:read' },
      { id: 'estimates', label: 'Estimates', permission: 'billing:read' },
      { id: 'stripe', label: 'Stripe Payments', permission: 'billing:read' },
      { id: 'stripereports', label: 'Stripe Reports', permission: 'billing:read' },
      { id: 'paymentanalytics', label: 'Payment Analytics', permission: 'billing:read' },
      { id: 'scheduling', label: 'Scheduling', permission: 'scheduling:read' },
      { id: 'telemedicine', label: 'Telemedicine', permission: 'telemed:read' },
      { id: 'insurance', label: 'Insurance', permission: 'billing:read' },
      { id: 'personnel', label: 'Personnel', permission: 'personnel:read' },
      { id: 'leave', label: 'Leave Requests', permission: 'leave:read' },
      { id: 'personnelactions', label: 'Personnel Actions', permission: 'personnel:read' },
      { id: 'crm', label: 'ZignaGenix CRM', permission: 'crm:read' },
      { id: 'crmimport', label: 'CRM Import/Upload', permission: 'crm:create' },
      { id: 'gohighlevel', label: 'GoHighLevel', permission: 'crm:read' },
      { id: 'notifications', label: 'Notifications', permission: 'notifications:read' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { id: 'usermanagement', label: 'User Management', permission: 'users:read' },
      { id: 'rolemanagement', label: 'Role Management', permission: 'users:read' },
      { id: 'sidebarvisibility', label: 'Sidebar Visibility', permission: 'users:read' },
      { id: 'adminaudit', label: 'Admin Audit Trail', permission: 'audit:read' },
      { id: 'dataexport', label: 'Data Export', permission: 'export:clinical' },
      { id: 'portal', label: 'Patient Portal Admin', permission: 'portal:manage' },
      { id: 'patientportal', label: 'Patient Portal View', permission: 'portal:manage' },
      { id: 'totp', label: '2FA Setup', permission: 'totp:setup' },
      { id: 'awscloud', label: 'AWS Cloud', permission: 'settings:update' },
      { id: 'securityupdates', label: 'Security Updates', permission: 'settings:read' },
      { id: 'audit', label: 'Audit Log', permission: 'audit:read' },
      { id: 'settings', label: 'Settings', permission: 'settings:read' },
    ],
  },
];

// DERIVED from src/lib/roleCatalog.js. This file's own 11-entry array was the
// most stale of the six lists — missing TWELVE roles including board_of_directors
// (task #92), sales, and patient_experience. Roles with zero permissions are
// included on purpose: the simulator renders an empty sidebar, which makes the
// emptiness visible rather than theoretical.
const ALL_ROLES = previewableRoleKeys();

// Compact labels for the matrix column headers (e.g. "Net Eng"), declared as
// `shortLabel` in the catalog so the dense-table need is recorded rather than
// implied by a second hardcoded map.
const ROLE_LABELS = previewableRoleLabels();

export default function SidebarVisibilityPage({ user }) {
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState('physician');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const result = await window.zignagenixAPI.getRBACData();
        if (result && !result.error) {
          setRolePermissions(result.rolePermissions || {});
        }
      } catch (err) {
        console.error('Error loading RBAC data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const currentPerms = useMemo(() => rolePermissions[selectedRole] || [], [selectedRole, rolePermissions]);

  const visibleItemCount = useMemo(() => {
    let count = 0;
    SIDEBAR_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        if (currentPerms.includes(item.permission)) count++;
      });
    });
    return count;
  }, [currentPerms]);

  const totalItems = SIDEBAR_SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

  // Build a matrix view: roles across top, items down the side
  const matrixView = useMemo(() => {
    return SIDEBAR_SECTIONS.map(section => ({
      label: section.label,
      items: section.items.map(item => ({
        ...item,
        roleVisibility: ALL_ROLES.reduce((acc, role) => {
          acc[role] = (rolePermissions[role] || []).includes(item.permission);
          return acc;
        }, {}),
      })),
    }));
  }, [rolePermissions]);

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <p style={{ color: '#6B7280' }}>Loading sidebar visibility...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...NEU.statIcon, background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }}>
            <Menu size={22} />
          </div>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Sidebar Visibility</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              Per-role menu visibility &bull; {totalItems} sidebar items &bull; {ALL_ROLES.length} roles
            </p>
          </div>
        </div>
        <PrintButton pageTitle="Sidebar Visibility" />
      </div>

      {/* Info banner */}
      <div style={{ ...NEU.cardSm, padding: '12px 16px', marginBottom: 24, background: '#eff6ff',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <Shield size={16} color="#1e40af" />
        <span style={{ fontSize: 13, color: '#1e40af' }}>
          Sidebar visibility is controlled by the RBAC permission assigned to each menu item.
          A user sees a menu item only if their role includes the required permission.
          To change visibility, modify the role's permissions on the Role Management page.
        </span>
      </div>

      {/* Role Quick View */}
      <div style={{ ...NEU.card, padding: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginRight: 8 }}>View as role:</span>
          {ALL_ROLES.map(role => (
            <button key={role} onClick={() => setSelectedRole(role)}
              style={{
                padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                ...(selectedRole === role
                  ? { background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff',
                      boxShadow: NEU.shadow.insetSm }
                  : { background: NEU.bg, color: '#4b5563', boxShadow: NEU.shadow.xs }),
              }}>
              {ROLE_LABELS[role]}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Role Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24, marginBottom: 32 }}>
        {/* Simulated sidebar */}
        <div style={{ ...NEU.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
              Sidebar Preview: {ROLE_LABELS[selectedRole]}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.8 }}>
              {visibleItemCount} of {totalItems} items visible
            </p>
          </div>
          <div style={{ maxHeight: 500, overflow: 'auto' }}>
            {SIDEBAR_SECTIONS.map(section => {
              const visibleItems = section.items.filter(i => currentPerms.includes(i.permission));
              if (visibleItems.length === 0) return null;
              return (
                <div key={section.label}>
                  <div style={{ padding: '10px 16px 4px', fontSize: 10, fontWeight: 700,
                    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {section.label}
                  </div>
                  {visibleItems.map(item => (
                    <div key={item.id} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 13, color: '#374151', borderBottom: '1px solid rgba(186,199,216,0.15)' }}>
                      <Eye size={14} color={NEU.teal} />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Hidden items */}
        <div style={{ ...NEU.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', background: '#fef2f2', borderBottom: '1px solid rgba(186,199,216,0.3)' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#991b1b' }}>
              Hidden from {ROLE_LABELS[selectedRole]}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#991b1b', opacity: 0.8 }}>
              {totalItems - visibleItemCount} items restricted
            </p>
          </div>
          <div style={{ maxHeight: 500, overflow: 'auto' }}>
            {SIDEBAR_SECTIONS.map(section => {
              const hiddenItems = section.items.filter(i => !currentPerms.includes(i.permission));
              if (hiddenItems.length === 0) return null;
              return (
                <div key={section.label}>
                  <div style={{ padding: '10px 16px 4px', fontSize: 10, fontWeight: 700,
                    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {section.label}
                  </div>
                  {hiddenItems.map(item => (
                    <div key={item.id} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 13, color: '#9ca3af', borderBottom: '1px solid rgba(186,199,216,0.15)' }}>
                      <EyeOff size={14} color="#d1d5db" />
                      <span style={{ textDecoration: 'line-through' }}>{item.label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'monospace', color: '#d1d5db' }}>
                        {item.permission}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Full Matrix View */}
      <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1f2937', marginBottom: 16 }}>Full Visibility Matrix</h2>
      <div style={{ ...NEU.tableWrap, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left', position: 'sticky', left: 0, background: NEU.bg, zIndex: 1 }}>
                MENU ITEM
              </th>
              {ALL_ROLES.map(role => (
                <th key={role} style={{ ...NEU.th, padding: '10px 8px', textAlign: 'center', fontSize: 9, whiteSpace: 'nowrap' }}>
                  {ROLE_LABELS[role].toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixView.map(section => (
              <React.Fragment key={section.label}>
                <tr>
                  <td colSpan={ALL_ROLES.length + 1}
                    style={{ padding: '8px 12px', fontWeight: 700, fontSize: 11, color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: 0.5, background: 'rgba(186,199,216,0.15)' }}>
                    {section.label}
                  </td>
                </tr>
                {section.items.map(item => (
                  <tr key={item.id}>
                    <td style={{ ...NEU.td, padding: '8px 12px', fontSize: 13, fontWeight: 500,
                      position: 'sticky', left: 0, background: NEU.bg, zIndex: 1 }}>
                      {item.label}
                      <span style={{ display: 'block', fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>
                        {item.permission}
                      </span>
                    </td>
                    {ALL_ROLES.map(role => (
                      <td key={role} style={{ ...NEU.td, padding: '8px', textAlign: 'center' }}>
                        {item.roleVisibility[role] ? (
                          <Check size={14} color="#16a34a" />
                        ) : (
                          <XIcon size={14} color="#e5e7eb" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
